import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { fullSrc, thumbSrc } from './gallery';

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// Full-screen preview carousel. `index` is the open item, or null when closed. Focus is
// trapped inside while open and handed back to the element that opened it on close.
export default function Lightbox({ items, index, onChange, onClose, returnTo }) {
  const open = index !== null && index !== undefined;
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const stripRef = useRef(null);
  const openerRef = useRef(null);
  const touchRef = useRef(null);
  const n = items.length;

  const go = useCallback((d) => onChange((index + d + n) % n), [index, n, onChange]);

  // Remember the opener, lock page scroll, focus the close button; undo all of it on close.
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = returnTo?.current || document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
      const el = openerRef.current;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preload the neighbours so next/previous shows at once.
  useEffect(() => {
    if (!open) return;
    [index - 1, index + 1].forEach((i) => { const img = new Image(); img.src = fullSrc(items[(i + n) % n].f); });
  }, [open, index, items, n]);

  // Keep the active thumbnail visible by scrolling the strip itself (never the page).
  useEffect(() => {
    const strip = stripRef.current;
    if (!open || !strip) return;
    const btn = strip.children[index];
    if (!btn) return;
    const left = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [open, index]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
    if (e.key === 'Home') { e.preventDefault(); onChange(0); return; }
    if (e.key === 'End') { e.preventDefault(); onChange(n - 1); return; }
    if (e.key === 'Tab') {
      const nodes = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (!nodes.length) return;
      const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  const onTouchStart = (e) => { const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const s = touchRef.current; touchRef.current = null;
    if (!s) return;
    const t = e.changedTouches[0]; const dx = t.clientX - s.x; const dy = t.clientY - s.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) go(dx < 0 ? 1 : -1);
  };

  // A click on the backdrop or the empty stage around the image closes; clicks on controls do not.
  const onBackdrop = (e) => { if (e.target === e.currentTarget || e.target.dataset.kzBackdrop) onClose(); };

  if (!open) return null;
  const item = items[index];

  return createPortal(
    <div className="kz-lb" role="dialog" aria-modal="true" aria-label={`Console screenshots: ${item.t}`}
      ref={dialogRef} onKeyDown={onKeyDown} onClick={onBackdrop}>
      <div className="kz-lb-top">
        <span className="kz-lb-count" aria-live="polite">{index + 1} / {n}</span>
        <button type="button" className="kz-lb-close" ref={closeRef} onClick={onClose} aria-label="Close preview">
          <i className="fas fa-xmark" aria-hidden="true" /><span>Close</span>
        </button>
      </div>
      <div className="kz-lb-stage" data-kz-backdrop="1" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button type="button" className="kz-lb-nav is-prev" onClick={() => go(-1)} aria-label="Previous screenshot">
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>
        <figure className="kz-lb-figure" data-kz-backdrop="1">
          <img key={item.f} src={fullSrc(item.f)} alt={`Kizashi console: ${item.t} page`} width="1440" height="1000" />
          <figcaption><b>{item.t}</b> {item.d}</figcaption>
        </figure>
        <button type="button" className="kz-lb-nav is-next" onClick={() => go(1)} aria-label="Next screenshot">
          <i className="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
      <div className="kz-lb-strip" ref={stripRef} role="group" aria-label="All screenshots">
        {items.map((it, i) => (
          <button type="button" key={it.f} className={`kz-lb-thumb ${i === index ? 'is-on' : ''}`}
            aria-current={i === index ? 'true' : undefined} aria-label={`${i + 1}: ${it.t}`} onClick={() => onChange(i)}>
            <img src={thumbSrc(it.f)} alt="" width="120" height="83" loading="lazy" decoding="async" />
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
